/**
 * 新建学生对话框（student-registry-admin-ui:SC-09/SC-10，design 前端页面变更）。
 *
 * 字段顺序对齐原型：姓名→性别→年级/学段→归属校区→科目（可选）→监护人称呼→
 * 与孩子关系→共用手机号→学生标签（可选，回车添加）。无学号输入、无报名分数。
 * 底部说明：仅建主档，不产生报名、不赠试课。
 * 同手机号多孩合法：撞号静默放行，无确认弹窗（对齐原型）。
 */
import { useEffect, useState } from 'react';
import { Form, Input, Select, Typography } from 'antd';
import { message } from '../../utils/antd-message';
import { DraggableModal } from '../../components/DraggableModal';
import {
  studentsApi,
  type CreateStudent,
  type GuardianRelationship,
} from '../../api/students.api';
import { resolveDefaultCampusId, useCampusOptions } from '../../hooks/useCampusOptions';
import { useSession } from '../../auth/SessionContext';
import { useGlobalCampusScope } from '../../auth/GlobalCampusContext';
import { subjectsApi, type SubjectItem } from '../../api/subjects.api';
import { gradeStageLabel, toSelectOptions } from '../../api/labels';
import { ApiError } from '../../api/transport';

const RELATIONSHIPS: { value: GuardianRelationship; label: string }[] = [
  { value: 'FATHER', label: '父亲' },
  { value: 'MOTHER', label: '母亲' },
  { value: 'GRANDPA', label: '爷爷' },
  { value: 'GRANDMA', label: '奶奶' },
  { value: 'OTHER', label: '其他' },
];

const MOBILE_RE = /^(\+?86)?1\d{10}$/;

export function StudentCreateModal({
  open,
  onClose,
  onCreated,
}: {
  open: boolean;
  onClose: () => void;
  onCreated: () => void;
}) {
  const [form] = Form.useForm<CreateStudent & { guardianName?: string; guardianRelationship?: GuardianRelationship }>();
  const [loading, setLoading] = useState(false);
  const [subjects, setSubjects] = useState<SubjectItem[]>([]);
  // 校区选项经共享 hook（ACTIVE + 按数据范围过滤 + numericCode 排序，收编自拉口径）
  const { campuses: campusList, loading: campusLoading } = useCampusOptions(open);
  const { permissions } = useSession();
  const { scope } = useGlobalCampusScope();
  const defaultCampusId = resolveDefaultCampusId(permissions, scope);

  async function loadOptions() {
    try {
      // 科目下拉按「本校 ∨ 机构通用」收敛（随全局校区口径）
      const subjectResult = await subjectsApi.listSubjects({
        status: 'ACTIVE',
        pageSize: 100,
        campusId: scope === 'ALL' ? undefined : scope,
      });
      setSubjects(subjectResult.items);
    } catch {
      // 降级：选项加载失败不阻断表单
    }
  }

  // 弹窗打开且校区选项就绪后回填默认校区（非总部单校区授权或总部锁定具体校区时预选）：
  // 仅在字段为空时写入不覆盖用户已选；默认校区不在 ACTIVE 选项中时不预选
  useEffect(() => {
    if (
      open &&
      !campusLoading &&
      defaultCampusId &&
      campusList.some((c) => c.id === defaultCampusId) &&
      !form.getFieldValue('homeCampusId')
    ) {
      form.setFieldValue('homeCampusId', defaultCampusId);
    }
  }, [open, campusLoading, campusList, defaultCampusId, form]);

  async function handleOk() {
    try {
      const values = await form.validateFields();
      setLoading(true);
      await studentsApi.createStudent({
        name: values.name,
        gender: values.gender,
        gradeStage: values.gradeStage,
        homeCampusId: values.homeCampusId,
        subjectIds: values.subjectIds,
        tags: values.tags,
        guardianMobile: values.guardianMobile,
        guardians: [
          {
            name: (values as { guardianName?: string }).guardianName ?? '',
            relationship: (values as { guardianRelationship?: GuardianRelationship }).guardianRelationship ?? 'FATHER',
          },
        ],
      });
      message.success('已建立学生档案，尚未产生报名，可在「学生与报名」列表为其代家长报名');
      form.resetFields();
      onCreated();
    } catch (err) {
      if (err instanceof ApiError) {
        message.error(err.message);
      } else if (err instanceof Error && err.message.includes('validation')) {
        // antd form 校验错误已内联展示
      } else {
        message.error('新建学生失败');
      }
    } finally {
      setLoading(false);
    }
  }

  return (
    <DraggableModal
      title="新建学生"
      open={open}
      onOk={() => handleOk()}
      onCancel={onClose}
      okText="保存"
      confirmLoading={loading}
      destroyOnHidden
      width={640}
      afterOpenChange={(o) => o && void loadOptions()}
    >
      <Form
        form={form}
        layout="vertical"
        data-testid="form-create-student"
        initialValues={{ gender: 'MALE', gradeStage: 'PRIMARY_LOW', guardianRelationship: 'FATHER' }}
      >
        <div style={{ display: 'flex', gap: 16, flexWrap: 'wrap' }}>
          <Form.Item name="name" label="姓名" rules={[{ required: true, message: '请输入姓名' }]}>
            <Input style={{ width: 160 }} data-testid="input-name" />
          </Form.Item>
          <Form.Item name="gender" label="性别" rules={[{ required: true, message: '请选择性别' }]}>
            <Select
              style={{ width: 100 }}
              options={[
                { value: 'MALE', label: '男' },
                { value: 'FEMALE', label: '女' },
              ]}
              data-testid="input-gender"
            />
          </Form.Item>
          <Form.Item name="gradeStage" label="年级/学段" rules={[{ required: true, message: '请选择学段' }]}>
            <Select style={{ width: 150 }} options={toSelectOptions(gradeStageLabel)} data-testid="input-grade-stage" />
          </Form.Item>
          <Form.Item name="homeCampusId" label="归属校区" rules={[{ required: true, message: '请选择校区' }]}>
            <Select
              style={{ width: 150 }}
              options={campusList.map((c) => ({ value: c.id, label: c.name }))}
              data-testid="input-campus"
            />
          </Form.Item>
          <Form.Item name="subjectIds" label="科目（可选）">
            <Select
              mode="multiple"
              style={{ width: 220 }}
              options={subjects.map((s) => ({ value: s.id, label: s.name }))}
              placeholder="选择意向科目"
              data-testid="input-subjects"
            />
          </Form.Item>
        </div>
        <div style={{ display: 'flex', gap: 16, flexWrap: 'wrap' }}>
          <Form.Item
            name="guardianName"
            label="监护人称呼"
            rules={[{ required: true, message: '请输入监护人称呼' }]}
          >
            <Input style={{ width: 140 }} data-testid="input-guardian-name" />
          </Form.Item>
          <Form.Item name="guardianRelationship" label="与孩子关系" rules={[{ required: true }]}>
            <Select style={{ width: 120 }} options={RELATIONSHIPS} data-testid="input-guardian-relationship" />
          </Form.Item>
          <Form.Item
            name="guardianMobile"
            label="共用手机号"
            rules={[
              { required: true, message: '请输入手机号' },
              { pattern: MOBILE_RE, message: '请输入正确的手机号' },
            ]}
          >
            <Input style={{ width: 160 }} data-testid="input-mobile" placeholder="11 位手机号" />
          </Form.Item>
        </div>
        <Form.Item name="tags" label="学生标签（可选）">
          <Select mode="tags" tokenSeparators={[',']} placeholder="回车添加标签" data-testid="input-tags" />
        </Form.Item>
        <Typography.Paragraph type="secondary" style={{ fontSize: 12, marginBottom: 0 }}>
          仅建立学生主档，不产生报名记录、不赠送课时；如需报名请在「学生与报名」列表点击“代家长报名”。
        </Typography.Paragraph>
      </Form>
    </DraggableModal>
  );
}
